import { useEffect, useRef, useState } from "react";

/**
 * iOS-style pull-to-refresh hook.
 *
 * Usage:
 *   const { indicator, refreshing } = usePullToRefresh(async () => {
 *     await fetchData();
 *   });
 *   return <div>{indicator}<MyPage /></div>;
 *
 * Triggers only when the page is scrolled to the very top, the user pulls
 * down past the threshold, and then releases. Works on touch devices
 * (iOS Safari, Android Chrome) — silently no-ops on desktop with a mouse.
 *
 * The page must NOT have CSS `overscroll-behavior-y: contain` higher up in
 * the tree, but the global stylesheet uses `overflow-x: hidden` only.
 */
const THRESHOLD = 70; // px to fully arm the trigger
const MAX_PULL = 120; // visual cap

export function usePullToRefresh(onRefresh, { enabled = true } = {}) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const armed = useRef(false);
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  useEffect(() => {
    if (!enabled) return undefined;
    const isTop = () => (window.scrollY || document.documentElement.scrollTop || 0) <= 0;

    const onTouchStart = (e) => {
      if (refreshing) return;
      if (!isTop()) { startY.current = null; armed.current = false; return; }
      startY.current = e.touches[0].clientY;
      armed.current = true;
    };
    const onTouchMove = (e) => {
      if (!armed.current || startY.current == null || refreshing) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy <= 0) { setPull(0); return; }
      // Only consume the gesture if user is actively pulling down at the top
      if (!isTop()) { armed.current = false; setPull(0); return; }
      const eased = Math.min(MAX_PULL, dy * 0.55);
      setPull(eased);
      if (e.cancelable && eased > 6) e.preventDefault();
    };
    const onTouchEnd = async () => {
      if (!armed.current) return;
      armed.current = false;
      const fired = pull >= THRESHOLD;
      startY.current = null;
      if (fired) {
        setRefreshing(true);
        setPull(THRESHOLD);
        try { await onRefreshRef.current?.(); }
        finally {
          setRefreshing(false);
          setPull(0);
        }
      } else {
        setPull(0);
      }
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: false });
    window.addEventListener("touchend", onTouchEnd);
    window.addEventListener("touchcancel", onTouchEnd);
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [enabled, refreshing, pull]);

  const progress = Math.min(1, pull / THRESHOLD);
  const indicator = (
    <PullIndicator pull={pull} refreshing={refreshing} progress={progress} />
  );

  return { indicator, refreshing, pulling: pull > 0 };
}

function PullIndicator({ pull, refreshing, progress }) {
  // Only render on small screens — desktop has no pull gesture.
  return (
    <div
      aria-hidden={!refreshing && pull === 0}
      className="lg:hidden pointer-events-none fixed inset-x-0 top-0 z-50 flex justify-center"
      style={{
        transform: `translateY(${refreshing ? 14 : Math.max(0, pull - 36)}px)`,
        opacity: refreshing ? 1 : Math.min(1, progress * 1.2),
        transition: refreshing ? "transform 200ms ease-out, opacity 200ms ease-out" : "opacity 120ms linear",
        paddingTop: "env(safe-area-inset-top)",
      }}
      data-testid="pull-to-refresh-indicator"
    >
      <div className="mt-1 h-9 w-9 rounded-full bg-card border border-border/60 shadow-soft grid place-items-center">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`text-primary ${refreshing ? "animate-spin" : ""}`}
          style={{ transform: refreshing ? "none" : `rotate(${progress * 270}deg)`, transition: "transform 80ms linear" }}
        >
          <path d="M21 12a9 9 0 1 1-3-6.7" />
          <path d="M21 4v5h-5" />
        </svg>
      </div>
    </div>
  );
}

export default usePullToRefresh;
